/**
 * BillingEncountersList Component
 *
 * Displays a responsive list of billing encounters with pagination.
 * Uses CSS Grid layout that adapts from full table to stacked cards on mobile.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  IconButton,
  Paper,
  Skeleton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { OrgBillingEncounterResponse } from '@store/generated/billing';

import { useBillingEncounters } from '../../../store';

import { BillingEncounterRow } from './BillingEncounterRow';
import { BillingEncountersTableHeader } from './BillingEncountersTableHeader';
import type { TableLayoutMode } from './types';
import { getGridTemplateColumns, GRID_GAP } from './types';

interface BillingEncountersListProps {
  filters: {
    patient?: string;
    from?: string;
    to?: string;
    status?: string;
    providers?: string;
    hasClaim?: string;
    readyToBill?: string;
  };
  sort?: string;
  order: 'asc' | 'desc';
  page: number;
  perPage: number;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
  onSort: (field: string) => void;
  onSelectedEncountersChange?: (encounters: OrgBillingEncounterResponse[]) => void;
  onCreateClaim?: (encounter: OrgBillingEncounterResponse) => void;
}

export const BillingEncountersList: React.FC<BillingEncountersListProps> = ({
  filters,
  sort,
  order,
  page,
  perPage,
  onPageChange,
  onSelectedEncountersChange,
  onCreateClaim,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Responsive table layout based on screen width
  const isXl = useMediaQuery(theme.breakpoints.up('xl'));
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));

  const tableLayout: TableLayoutMode = useMemo(() => {
    if (isXl) return 'full';
    if (isLg) return 'standard';
    if (isMd) return 'compact';
    return 'card';
  }, [isXl, isLg, isMd]);

  // Selection tracking
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Map sort field to API field
  const apiSortBy = useMemo((): 'scheduled_for' | 'created_at' => {
    if (sort === 'date' || sort === 'scheduled_for') return 'scheduled_for';
    if (sort === 'created_at') return 'created_at';
    return 'scheduled_for';
  }, [sort]);

  // Parse boolean filter values from URL strings
  const hasClaimFilter = useMemo(() => {
    if (filters.hasClaim === 'true') return true;
    if (filters.hasClaim === 'false') return false;
    return undefined;
  }, [filters.hasClaim]);

  const { 
    encounters,
    totalCount,
    currentPage,
    totalPages,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useBillingEncounters({
    patientId: filters.patient,
    providerIds: filters.providers,
    status: filters.status,
    hasClaim: hasClaimFilter,
    readyToBill: filters.readyToBill,
    dateFrom: filters.from,
    dateTo: filters.to,
    page,
    perPage,
    sortBy: apiSortBy,
    sortOrder: order,
  });

  const isEmpty = !isLoading && encounters.length === 0;
  const isListLoading = isLoading || isFetching;
  const skeletonRowCount = Math.min(12, Math.max(6, perPage));

  const tableSkeletonLayout = tableLayout === 'card'
    ? 'card'
    : tableLayout === 'full' || tableLayout === 'standard' || tableLayout === 'compact'
      ? tableLayout
      : 'full';

  const showProvider = tableSkeletonLayout === 'full' || tableSkeletonLayout === 'standard';
  const showService = tableSkeletonLayout === 'full' || tableSkeletonLayout === 'standard';
  const showCptCharges = tableSkeletonLayout === 'full' || tableSkeletonLayout === 'standard';

  const renderTableSkeletonRow = (key: number) => {
    if (tableSkeletonLayout === 'card') {
      return (
        <Box
          key={key}
          sx={{
            px: 2,
            py: 1.25,
            borderBottom: `1px solid ${theme.palette.grey[100]}`,
            bgcolor: 'transparent',
            '&:last-child': { borderBottom: 'none' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
            <Skeleton variant="circular" width={28} height={28} />
            <Skeleton variant="text" width="15%" />
            <Skeleton variant="text" width="12%" />
            <Skeleton variant="rectangular" width={56} height={18} sx={{ borderRadius: 999 }} />
            <Skeleton variant="rectangular" width={66} height={18} sx={{ borderRadius: 999 }} />
            <Skeleton variant="text" width={24} height={18} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <Skeleton variant="circular" width={20} height={20} />
            <Skeleton variant="text" width="22%" />
            <Skeleton variant="text" width={8} />
            <Skeleton variant="circular" width={20} height={20} />
            <Skeleton variant="text" width="26%" />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Skeleton variant="text" width="18%" />
            <Skeleton variant="text" width="12%" />
            <Skeleton variant="text" width="12%" />
            <Skeleton variant="rectangular" width={44} height={18} sx={{ borderRadius: 1 }} />
          </Box>
        </Box>
      );
    }

    return (
      <Box
        key={key}
        sx={{
          px: 2,
          py: 0.75,
          borderBottom: `1px solid ${theme.palette.grey[100]}`,
          '&:last-child': { borderBottom: 'none' },
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: getGridTemplateColumns(tableSkeletonLayout),
            gap: GRID_GAP,
            alignItems: 'center',
          }}
        >
          <Skeleton variant="circular" width={32} height={32} />
          <Box sx={{ minWidth: 0 }}>
            <Skeleton variant="text" width="70%" />
            <Skeleton variant="text" width="38%" />
          </Box>
          {showProvider && <Skeleton variant="text" width="85%" />}
          <Skeleton variant="text" width="88%" />
          {showService && <Skeleton variant="text" width="72%" />}
          {showCptCharges && <Skeleton variant="text" width="68%" />}
          <Skeleton variant="rectangular" width={44} height={20} sx={{ borderRadius: 1 }} />
          <Skeleton variant="text" width="70%" />
          <Skeleton variant="circular" width={10} height={10} />
          <Skeleton variant="rectangular" width={34} height={18} sx={{ borderRadius: 1 }} />
        </Box>
      </Box>
    );
  };

  // Pagination
  const hasPrevious = currentPage > 1;
  const hasMore = currentPage < totalPages;

  const handlePreviousPage = useCallback(() => {
    if (hasPrevious && !isLoading) {
      onPageChange(Math.max(1, currentPage - 1));
    }
  }, [hasPrevious, isLoading, currentPage, onPageChange]);

  const handleNextPage = useCallback(() => {
    if (hasMore && !isLoading) {
      onPageChange(currentPage + 1);
    }
  }, [hasMore, isLoading, currentPage, onPageChange]);

  // Selection handlers
  const handleSelectChange = useCallback(
    (encounterId: string, selected: boolean) => {
      setSelectedIds(prev => {
        const next = new Set(prev);
        if (selected) {
          next.add(encounterId);
        } else {
          next.delete(encounterId);
        }
        // Notify parent
        if (onSelectedEncountersChange) {
          const selectedEncounters = encounters.filter(e => next.has(e.encounter_id));
          onSelectedEncountersChange(selectedEncounters);
        }
        return next;
      });
    },
    [encounters, onSelectedEncountersChange],
  );

  // Row click handler — navigate to encounter detail page
  const handleRowClick = useCallback(
    (encounter: OrgBillingEncounterResponse) => {
      navigate(`/billing/encounter/${encounter.encounter_id}`);
    },
    [navigate],
  );

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={refetch}>
            Retry
          </Button>
        }
      >
        <AlertTitle>Error loading encounters</AlertTitle>
        Failed to load billing encounters. Please try again.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Summary */}
      <Box sx={{ mb: 2 }}>
        {!isListLoading && (
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            {totalCount} {totalCount === 1 ? 'encounter' : 'encounters'}
            {isFetching && !isLoading && ' (updating...)'}
          </Typography>
        )}
      </Box>

      {/* Encounters Table */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <BillingEncountersTableHeader layout={tableLayout} />

        {isListLoading ? (
          <Box>
            {Array.from({ length: skeletonRowCount }).map((_, i) => (
              renderTableSkeletonRow(i)
            ))}
          </Box>
        ) : isEmpty ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
              px: 2,
            }}
          >
            <Typography variant="h6" sx={{ mb: 1, color: theme.palette.text.secondary }}>
              No encounters found
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
              Try adjusting your filters or date range
            </Typography>
          </Box>
        ) : (
          <Box>
            {encounters.map(encounter => (
              <BillingEncounterRow
                key={encounter.encounter_id}
                encounter={encounter}
                layout={tableLayout}
                selected={selectedIds.has(encounter.encounter_id)}
                onSelectChange={onSelectedEncountersChange ? handleSelectChange : undefined}
                onCreateClaim={onCreateClaim}
                onRowClick={handleRowClick}
              />
            ))}
          </Box>
        )}

        {/* Pagination */}
        {!isEmpty && !isListLoading && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
              borderTop: `1px solid ${theme.palette.grey[200]}`,
              bgcolor: theme.palette.grey[50],
            }}
          >
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              Page {currentPage} of {totalPages} ({totalCount} total)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                onClick={handlePreviousPage}
                disabled={!hasPrevious || isFetching}
                size="small"
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                onClick={handleNextPage}
                disabled={!hasMore || isFetching}
                size="small"
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
